export type PostUploadErrorCode =
  | "post/image-picker-failed"
  | "post/image-manipulation-failed"
  | "post/file-not-found"
  | "post/file-read-failed"
  | "post/file-empty"
  | "post/file-too-large"
  | "post/download-url-failed"
  | "storage/unauthenticated"
  | "storage/unauthorized"
  | "storage/quota-exceeded"
  | "storage/canceled"
  | "storage/timeout"
  | "storage/unknown";

export class PostUploadError extends Error {
  constructor(
    public readonly code: PostUploadErrorCode,
    message: string,
    options?: { cause?: unknown }
  ) {
    super(message, options);
    this.name = "PostUploadError";
  }
}

export function classifyPendingImageAsset(asset: {
  uri?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
}, maxBytes: number): "accepted" | "invalid" | "too-large" {
  if (!asset.uri || (asset.mimeType && !asset.mimeType.startsWith("image/"))) return "invalid";
  if (typeof asset.fileSize === "number" && Number.isFinite(asset.fileSize) && asset.fileSize > maxBytes) {
    return "too-large";
  }
  return "accepted";
}

export function createPostUploadError(
  code: PostUploadErrorCode,
  message: string,
  cause?: unknown
) {
  return new PostUploadError(code, message, { cause });
}

export type ReadableUploadFile = {
  exists: boolean;
  size: number;
  bytes(): Promise<Uint8Array>;
  close?: () => void;
};

function assertReadableSize(size: number, maxBytes: number) {
  if (!Number.isFinite(size) || size <= 0) {
    throw createPostUploadError("post/file-empty", "Le fichier est vide.");
  }
  if (size > maxBytes) {
    throw createPostUploadError("post/file-too-large", "Le fichier est trop volumineux.");
  }
}

export async function readUploadBytes(params: {
  uri: string;
  declaredSize?: number;
  maxBytes: number;
  createFile: (uri: string) => ReadableUploadFile;
}): Promise<Uint8Array> {
  if (Number.isFinite(params.declaredSize ?? NaN) && (params.declaredSize as number) > params.maxBytes) {
    throw createPostUploadError("post/file-too-large", "Le fichier est trop volumineux.");
  }

  let file: ReadableUploadFile;
  try {
    file = params.createFile(params.uri);
  } catch (error) {
    throw createPostUploadError(
      "post/file-read-failed",
      "Impossible d'ouvrir le fichier local.",
      error
    );
  }

  try {
    if (!file.exists) {
      throw createPostUploadError("post/file-not-found", "Le fichier local est introuvable.");
    }

    assertReadableSize(file.size, params.maxBytes);

    let bytes: Uint8Array;
    try {
      bytes = await file.bytes();
    } catch (error) {
      throw createPostUploadError(
        "post/file-read-failed",
        "Impossible de lire le fichier local.",
        error
      );
    }

    if (!(bytes instanceof Uint8Array)) {
      throw createPostUploadError(
        "post/file-read-failed",
        "Le fichier local n'a pas pu etre converti en octets."
      );
    }
    assertReadableSize(bytes.byteLength, params.maxBytes);
    return bytes;
  } finally {
    try {
      file.close?.();
    } catch {
      // A read/validation error remains the primary error.
    }
  }
}

export function waitForUpload<T>(params: {
  upload: Promise<T>;
  timeoutMs: number;
  controller?: AbortController;
  signal?: AbortSignal;
}): Promise<T> {
  if (params.signal?.aborted) {
    params.controller?.abort();
    return Promise.reject(createPostUploadError("storage/canceled", "L'envoi a ete annule."));
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const onAbort = () => {
      settleOnce({
        ok: false,
        error: createPostUploadError("storage/canceled", "L'envoi a ete annule."),
      });
    };
    const settleOnce = (outcome: { ok: true; value: T } | { ok: false; error: unknown }) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      params.signal?.removeEventListener("abort", onAbort);
      if (outcome.ok) resolve(outcome.value);
      else reject(outcome.error);
    };

    params.signal?.addEventListener("abort", onAbort, { once: true });

    timeoutId = setTimeout(() => {
      params.controller?.abort();
      const timeoutError = createPostUploadError(
        "storage/timeout",
        "Le delai d'envoi du fichier est depasse."
      );
      settleOnce({ ok: false, error: timeoutError });
    }, params.timeoutMs);

    params.upload.then(
      (value) => settleOnce({ ok: true, value }),
      (error) => settleOnce({ ok: false, error })
    );
  });
}

export function isCurrentPostImagePath(path: string, uid: string, postId: string) {
  const prefixes = [
    `post_images/${uid}/${postId}/`,
    `moderation-pending/${uid}/${postId}/`,
  ];
  return prefixes.some((prefix) =>
    path.startsWith(prefix) && path.length > prefix.length && !path.slice(prefix.length).includes("/")
  );
}

export async function cleanupCurrentPostImagePaths(params: {
  uid: string;
  postId: string;
  storagePaths: string[];
  remove: (storagePath: string) => Promise<void>;
}) {
  const ownedPaths = Array.from(
    new Set(
      params.storagePaths.filter((path) =>
        isCurrentPostImagePath(path, params.uid, params.postId)
      )
    )
  );
  await Promise.all(ownedPaths.map(params.remove));
}
